import { Refresh } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { ChangeEvent } from 'react';
import { useTranslate } from 'react-admin';

import { LiveLogRange } from 'types/LiveLogs';
import {
  formatClockTime,
  isLiveLogRange,
  LIVE_LOG_RANGES,
  LIVE_LOGS_AUTO_REFRESH_MS,
  LIVE_LOGS_I18N,
} from './liveLogs';
import { useLiveLogsQueryStatus } from './useLiveLogsQueryStatus';

interface LiveLogsToolbarProps {
  resource: string;
  range: LiveLogRange;
  onRangeChange: (range: LiveLogRange) => void;
  autoRefresh: boolean;
  onAutoRefreshChange: (autoRefresh: boolean) => void;
}

interface UpdatedHintProps {
  autoRefresh: boolean;
  updatedAt: number;
  page: number | undefined;
}

const UpdatedHint = ({ autoRefresh, updatedAt, page }: UpdatedHintProps) => {
  const translate = useTranslate();

  if (updatedAt === 0) {
    return null;
  }

  const time = formatClockTime(updatedAt);
  const key = !autoRefresh ? 'updated' : page !== undefined && page > 1 ? 'paused' : 'auto_refresh';

  return (
    <Typography variant="caption" component="p" sx={{ ml: 'auto', fontVariantNumeric: 'tabular-nums' }}>
      {translate(`${LIVE_LOGS_I18N}.hint.${key}`, { time })}
    </Typography>
  );
};

/** Range, Refresh and auto-refresh for the active source; one row that wraps on narrow screens. */
export const LiveLogsToolbar = ({
  resource,
  range,
  onRangeChange,
  autoRefresh,
  onAutoRefreshChange,
}: LiveLogsToolbarProps) => {
  const translate = useTranslate();
  const queryClient = useQueryClient();
  const { isFetching, updatedAt, page } = useLiveLogsQueryStatus(resource);

  // Only this source's lists. useRefresh() would also refetch the unit record itself.
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: [resource, 'getList'] });
  };

  const handleRangeChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (isLiveLogRange(event.target.value)) {
      onRangeChange(event.target.value);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
      <TextField
        select
        size="small"
        label={translate(`${LIVE_LOGS_I18N}.range.label`)}
        value={range}
        onChange={handleRangeChange}
        sx={{ minWidth: 200 }}
      >
        {LIVE_LOG_RANGES.map((option) => (
          <MenuItem key={option} value={option}>
            {translate(`${LIVE_LOGS_I18N}.range.options.${option}`)}
          </MenuItem>
        ))}
      </TextField>
      {/* Never disabled while fetching: a disabled button would drop focus on every auto-refresh. */}
      <Button
        size="small"
        startIcon={isFetching ? <CircularProgress size="1em" color="inherit" /> : <Refresh />}
        aria-busy={isFetching}
        onClick={refresh}
      >
        {translate('ra.action.refresh')}
      </Button>
      <FormControlLabel
        control={
          <Switch size="small" checked={autoRefresh} onChange={(_event, checked) => onAutoRefreshChange(checked)} />
        }
        label={translate(`${LIVE_LOGS_I18N}.auto_refresh`, { seconds: LIVE_LOGS_AUTO_REFRESH_MS / 1000 })}
        slotProps={{ typography: { variant: 'body2' } }}
      />
      <UpdatedHint autoRefresh={autoRefresh} updatedAt={updatedAt} page={page} />
    </Box>
  );
};
